import firebase from 'firebase/compat/app'
import axios from 'axios'
import config from '../config'
import router from '../routes'
import {
  Notification as NotificationModel,
  CalendarEvent
} from '../models'
import moment from 'moment-timezone'
import { formatPhoneNumberE164 } from './helper'
import { acceptIncomingCallAction, declineIncomingCallAction } from '../pmd/components/power_dialer/callActions'
import { tasksRouteNames } from '@/routes/constants/location'

export default class NotificationHelper {
  public static init(registration: ServiceWorkerRegistration) {
    if ('serviceWorker' in navigator) {
      const messaging = firebase.messaging()
      messaging.onMessage((message) => NotificationHelper.handleMessage(message, registration))
      navigator.serviceWorker.addEventListener('message', event => {
        if (event.data.customPushMessage) {
          NotificationHelper.handleBackgroundClick(event.data.customPushMessage, event.data.notificationAction)
        }
      })
    }
    this.requestPermission()
  }

  public static async requestPermission() {

    if ('serviceWorker' in navigator) {
      try {
        const messaging = firebase.messaging()
        const registration = await navigator.serviceWorker.getRegistration()
        const token = await messaging.getToken({ vapidKey: config.fcmPublicKey, serviceWorkerRegistration: registration })
        if (token) {
          axios.post(`${config.newBaseUrl}/api/token/update`, { token })
        }

      } catch (err) {
        console.log(err)
      }
    }
  }

  public static requestBrowserPermission() {
    Notification.requestPermission();
  }

  public static async handleMessage(message: { [key: string]: any }, registration: ServiceWorkerRegistration) {
    const flavor = process.env.VUE_APP_FLAVOR
    const data = message.data
    let title: string = ''
    let notification: Notification
    switch (data.type) {
      case 'sms':
        title = data.title ?? 'New message from ' + (data.fromName || data.fromNumber)
        notification = new Notification(title, {
          body: data.body,
          icon: data.logo
        })
        notification.onclick = event => {
          notification.close()
          window.focus()
          NotificationHelper.handleBackgroundClick(data)
        }
        break
      case 'email':
        title = data.title ?? 'New message from ' + (data.fromName || data.fromEmail)
        notification = new Notification(title, {
          body: data.body,
          icon: data.logo
        })
        notification.onclick = event => {
          notification.close()
          window.focus()
          NotificationHelper.handleBackgroundClick(data)
        }
        break
      case 'fb_messenger':
      case 'gmb':
        title = data.title ?? 'New message from ' + (data.fromName || 'unknown')
        notification = new Notification(title, {
          body: data.body,
          icon: data.logo
        })
        notification.onclick = event => {
          notification.close()
          window.focus()
          NotificationHelper.handleBackgroundClick(data)
        }
        break
      case 'call':
        title = data.title ?? `Incoming call from ${(data.name || 'unknown')}`;
        let formattedNumber: string;
        try {
          formattedNumber = formatPhoneNumberE164(data.fromNumber)
        } catch (error) {
          formattedNumber = "Unknown Number"
        }
        registration.showNotification(title, {
          body: `${formattedNumber} \n ${data.locationName}`,
          icon: data.logo,
          actions: [{
            action: 'accept',
            title: 'accept'
          }, {
            action: 'decline',
            title: 'decline'
          }],
          data,
        });
      break
      case 'review':
      case 'reviewReply':
        if (data.type === 'review') {
          title = data.title ?? 'New review from ' + data.reviewerName
        } else if (data.type === 'reviewReply') {
          title = data.title ?? data.reviewerName + ' replied to the review'
        }
        notification = new Notification(title, {
          body: data.body,
          icon: data.logo
        })
        notification.onclick = event => {
          notification.close()
          window.focus()
          NotificationHelper.handleBackgroundClick(data)
        }
        break
      case 'appointment':
        title = data.title ?? 'New appointment booked by' + data.name
        notification = new Notification(title, {
          body: data.body,
          icon: data.logo
        })
        notification.onclick = event => {
          notification.close()
          window.focus()
          NotificationHelper.handleBackgroundClick(data)
        }
        break
      case 'snapshot': {
        title = data.title ?? `${data.snapshotName} ${data.loadStatus && data.loadStatus === 'success'
            ? 'Success'
            : 'Failed'
          }!`
        notification = new Notification(title, {
          body: data.body ?? `Snapshot ${data.loadStatus && data.loadStatus === 'success'
              ? 'load completed'
              : 'failed'
            }!`
        })
        notification.onclick = event => {
          notification.close()
          window.focus()
          NotificationHelper.handleBackgroundClick(data)
        }
        break
      }
      case 'voicemail':
        title = data.title ?? 'New voicemail from ' + (data.fromName || data.fromNumber)
        notification = new Notification(title, {
          body: data.body,
          icon: data.logo
        })
        notification.onclick = event => {
          notification.close()
          window.focus()
          NotificationHelper.handleBackgroundClick(data)
        }
        break
      case 'smartlist':
        //title = 'Smart List Change' + (data.fromName || data.fromNumber)
        notification = new Notification(data.title, {
          body: data.body,
          icon: data.logo
        })
        if (data.listId) {
          notification.onclick = event => {
            notification.close()
            window.focus()
            if (router && router.currentRoute.params && router.currentRoute.params.smart_list === data.listId) router.go(0);
            else if (router) router.push({ name: 'smart_list', params: { smart_list: data.listId } });
          }
        }
        break
      case 'listings':
        notification = new Notification(data.title, {
          body: data.body,
          icon: data.logo
        })
        notification.onclick = event => {
          notification.close()
          window.focus()
          NotificationHelper.handleBackgroundClick(data)
        }
        break
      case 'reputation_datashake':
          notification = new Notification(data.title, {
            body: data.message,
            icon: data.logo
          })
          notification.onclick = event => {
            notification.close()
            window.focus()
            NotificationHelper.handleBackgroundClick(data)
          }
          break
      case 'custom':
        notification = new Notification(data.title, {
          body: data.body,
          icon: data.logo
        })
        notification.onclick = event => {
          notification.close()
          window.focus()
          NotificationHelper.handleBackgroundClick(data)
        }
        break
      case 'wordpress':
        notification = new Notification(data.title, {
          body: data.body,
          icon: data.logo
        })
        notification.onclick = event => {
          notification.close()
          window.focus()
          NotificationHelper.handleBackgroundClick(data)
        }
        break
      case 'reputation_gmb_review':
        notification = new Notification(data.title, {
          body: data.body,
          icon: data.logo
        })
        notification.onclick = event => {
          notification.close()
          window.focus()
          NotificationHelper.handleBackgroundClick(data)
        }
        break
    }
  }

  public static async handleBackgroundClick(data: { [key: string]: any }, notificationAction?: string) {
    NotificationModel.markRead(data.notificationId)
    switch (data.type) {
      case 'fb_messenger':
      case 'email':
      case 'sms':
      case 'gmb':
      case 'instagram':
      case 'voicemail':
      case 'conversation_assigned':
        router.push({
          name: 'contact_detail',
          params: {
            location_id: data.locationId,
            contact_id: data.contactId
          }
        })
        break
      case 'call': {
        router.push({
          name: 'contact_detail',
          params: {
            location_id: data.locationId,
            contact_id: data.contactId
          }
        })
        if (notificationAction === 'accept') {
          acceptIncomingCallAction()
        }
        if (notificationAction === 'decline') {
          declineIncomingCallAction()
        }
        break;
      }
      case 'task_assigned':
      case 'task_reminder':
          router.push({
            name: tasksRouteNames.default,
            params: {
              location_id: data.locationId
            }
          })
        break
      case 'review':
      case 'reviewReply':
        router.push({
          name: 'reviews',
          params: { location_id: data.locationId, review_id: data.reviewId }
        })
        break
      case 'appointment':
        const event = await CalendarEvent.getById(data.eventId)
        router.push({
          name: 'calendar-v2',
          params: {
            location_id: data.locationId
          },
          query: {
            calendar: event.calendarId,
            event_id: data.eventId,
            startDate: moment(event.startTime).valueOf()
          }
        })
        break
      case 'snapshot': {
        router.push({
          name: 'dashboard_default',
          params: {
            location_id: data.locationId
          }
        })
      }
      break;
      case 'custom': {
        switch (data?.actionType) {
          case 'instagram_story':{
            router.push({
              name: 'social-planner-dashboard-v2',
              params: {
                location_id: data.locationId
              }
            })
          }
            break;
          case 'external':{
            window.open(data.externalUrl)
          }
            break;
          default:
            break;
        }
      }
      case 'listings':
        router.push({
          name: 'reputation-listing-v2',
          params: {
            location_id: data.locationId
          },
          query: {
            utm_source: data?.utmSource,
            utm_campaign: data?.utmCampaign,
          }
        })
        break
      case 'reputation_datashake':
          router.push({
            name: 'reputation-settings-v2',
            params: {
              location_id: data.locationId
            },
            query: {
             tab: 'reputationIntegrations'
            }
          })
          break
      case 'wordpress':
          router.push({
            name: 'wordpress-v2',
            params: {
              location_id: data.locationId
            }
          })
        break
      case 'reputation_gmb_review':
        router.push({
          name: 'reputation-settings-v2',
          params: {
            location_id: data.locationId
          },
          query: {
            tab: 'reviewsAI'
          }
        })
        break
      // case 'call':
      // 	store.commit('incomingCall/set', {
      // 		from: data.fromNumber,
      // 		to: data.toNumber,
      // 		newCustomer: data.newCustomer,
      // 		appointmentId: data.appointmentId,
      // 		contactId: data.contactId,
      // 		locationId: data.locationId,
      // 	});
      // 	break;
    }
  }
}
